import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { FiMail, FiLock, FiAlertCircle, FiEye, FiEyeOff } from "react-icons/fi";
import { toast } from "react-toastify";

const Login = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username) {
      newErrors.username = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.username)) {
      newErrors.username = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    if (submitError) {
      setSubmitError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError("");
    try {
      const result = await login(formData.username, formData.password);
      if (!result?.success) {
        const message =
          result?.error ||
          "Unable to sign in. Please try again.";
        setSubmitError(message);
        toast.error(message);
      }
    } catch (error) {
      const message = "An unexpected error occurred. Please try again.";
      setSubmitError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-base-100 to-primary/10 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8 bg-base-100 p-8 rounded-2xl shadow-xl">
        {/* Logo and Title */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-primary">Welcome Back</h2>
          <p className="mt-2 text-base-content/60">Sign in to your account</p>
        </div>

        {/* Login Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Email Input */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Email</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FiMail
                    className={`${
                      errors.username ? "text-error" : "text-base-content/40"
                    }`}
                  />
                </span>
                <input
                  type="email"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`input input-bordered w-full pl-10 ${
                    errors.username ? "input-error" : ""
                  }`}
                  placeholder="Enter your email"
                />
                {errors.username && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <FiAlertCircle className="text-error" />
                  </div>
                )}
              </div>
              {errors.username && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.username}
                  </span>
                </label>
              )}
            </div>

            {/* Password Input */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Password</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FiLock
                    className={`${
                      errors.password ? "text-error" : "text-base-content/40"
                    }`}
                  />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`input input-bordered w-full pl-10 pr-20 ${
                    errors.password ? "input-error" : ""
                  }`}
                  placeholder="Enter your password"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-1">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="btn btn-ghost btn-sm p-1 hover:bg-transparent"
                  >
                    {showPassword ? (
                      <FiEyeOff className="text-base-content/40 hover:text-base-content/60" />
                    ) : (
                      <FiEye className="text-base-content/40 hover:text-base-content/60" />
                    )}
                  </button>
                  {errors.password && <FiAlertCircle className="text-error" />}
                </div>
              </div>
              {errors.password && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.password}
                  </span>
                </label>
              )}
            </div>
          </div>

          {submitError && (
            <div
              role="alert"
              className="alert alert-error text-sm py-3"
            >
              <FiAlertCircle className="shrink-0" />
              <span>{submitError}</span>
            </div>
          )}

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <label className="label cursor-pointer">
              <input
                type="checkbox"
                className="checkbox checkbox-primary checkbox-sm"
              />
              <span className="label-text ml-2">Remember me</span>
            </label>
            {/* <a href="#" className="text-sm text-primary hover:underline">
              Forgot password?
            </a> */}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className={`btn btn-primary w-full`}
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>

        {/* Footer */}
        <div className="text-center text-sm text-base-content/60">
          <p>Don't have an account? Contact your administrator</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
